import { useState, useMemo } from 'react';
import { useShelterStore } from '../../../store/shelterStore';
import { useAuthStore } from '../../../store/authStore';
import { notifications } from '../../../lib/notifications';
import type { Shelter, Survivor } from '../../../types/shelter';
import { IconSearch, IconX, IconUsers } from '@tabler/icons-react';

interface Props { shelter: Shelter; }

function age(dob: string): string {
  if (!dob) return '';
  const d = new Date(dob);
  if (isNaN(d.getTime())) return '';
  const yrs = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
  return `${yrs}y`;
}

export default function ShelterPortalRoster({ shelter }: Props) {
  const { activeIncident } = useAuthStore();
  const { survivors, families, checkOutSurvivor, loadShelters } = useShelterStore();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Survivor | null>(null);
  const [checkingOut, setCheckingOut] = useState(false);

  const active = useMemo(() =>
    survivors.filter(s => s.shelterId === String(shelter.id) && s.status === 'checked_in'),
    [survivors, shelter.id],
  );

  const preReg = useMemo(() =>
    survivors.filter(s => s.shelterId === String(shelter.id) && s.status === 'pre-registered'),
    [survivors, shelter.id],
  );

  const filtered = useMemo(() => {
    if (!search) return active;
    const q = search.toLowerCase();
    return active.filter(s => `${s.firstName} ${s.lastName}`.toLowerCase().includes(q));
  }, [active, search]);

  const incidentFamilies = useMemo(() =>
    families.filter(f => String(f.incidentId) === String(activeIncident?.id ?? '')),
    [families, activeIncident],
  );

  // Group by family
  const familyMap = useMemo(() => {
    const map = new Map<string, { family: typeof incidentFamilies[0]; members: Survivor[] }>();
    for (const fam of incidentFamilies) {
      const members = filtered.filter(s => s.familyId === String(fam.id));
      if (members.length > 0) map.set(String(fam.id), { family: fam, members });
    }
    return map;
  }, [incidentFamilies, filtered]);

  const ungrouped = useMemo(() =>
    filtered.filter(s => !s.familyId || !incidentFamilies.find(f => String(f.id) === s.familyId)),
    [filtered, incidentFamilies],
  );

  const handleCheckout = async () => {
    if (!selected) return;
    setCheckingOut(true);
    try {
      await checkOutSurvivor(selected.id);
      await loadShelters();
      notifications.show({ title: 'Checked Out', message: `${selected.firstName} ${selected.lastName} has been checked out.`, color: 'var(--color-primary-700)' });
      setSelected(null);
    } catch {
      notifications.show({ title: 'Error', message: 'Checkout failed.', color: 'var(--color-error)' });
    } finally {
      setCheckingOut(false);
    }
  };

  return (
    <div>
      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <IconSearch size={16} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#939598' }} />
        <input className="sp-input" style={{ paddingLeft: 38 }} placeholder="Search by name…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div style={{ fontSize: 12, color: '#7B7D7F', marginBottom: 8 }}>
        {filtered.length} resident{filtered.length !== 1 ? 's' : ''} checked in
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#939598', fontSize: 14 }}>
          {active.length === 0 ? 'No one checked in yet.' : 'No results match your search.'}
        </div>
      )}

      {/* Families */}
      {Array.from(familyMap.entries()).map(([famId, { family, members }]) => (
        <div className="sp-family-group" key={famId}>
          <div className="sp-family-header">
            <IconUsers size={14} />
            {family.familyName}
            <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: '#4B3FFF' }}>
              {members.length} member{members.length !== 1 ? 's' : ''}
            </span>
          </div>
          {members.map(s => (
            <div className="sp-survivor-card" key={s.id} onClick={() => setSelected(s)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div className="sp-survivor-name">{s.firstName} {s.lastName}</div>
                  <div className="sp-survivor-meta">
                    {age(s.dateOfBirth)}{age(s.dateOfBirth) && s.gender ? ' · ' : ''}{s.gender}
                    {s.cotAssignment ? ` · Cot ${s.cotAssignment}` : ''}
                  </div>
                  {s.specialNeeds && s.specialNeeds !== '[]' && (
                    <div style={{ fontSize: 12, color: '#b45309', marginTop: 3 }}>⚠ {s.specialNeeds.replace(/[\[\]"]/g, '')}</div>
                  )}
                </div>
                <span className="sp-badge sp-badge-green">In</span>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* Ungrouped */}
      {ungrouped.map(s => (
        <div className="sp-ungrouped-card" key={s.id} onClick={() => setSelected(s)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div className="sp-survivor-name">{s.firstName} {s.lastName}</div>
              <div className="sp-survivor-meta">
                {age(s.dateOfBirth)}{age(s.dateOfBirth) && s.gender ? ' · ' : ''}{s.gender}
                {s.cotAssignment ? ` · Cot ${s.cotAssignment}` : ''}
              </div>
              {s.specialNeeds && s.specialNeeds !== '[]' && (
                <div style={{ fontSize: 12, color: '#b45309', marginTop: 3 }}>⚠ {s.specialNeeds.replace(/[\[\]"]/g, '')}</div>
              )}
            </div>
            <span className="sp-badge sp-badge-green">In</span>
          </div>
        </div>
      ))}

      {/* Expected Arrivals (pre-registered) */}
      {preReg.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
            paddingBottom: 8, borderBottom: '1px solid #e2e8f0',
          }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#7c3aed' }}>Expected Arrivals</span>
            <span style={{
              background: '#f0eeff', color: '#7c3aed', fontSize: 12, fontWeight: 700,
              padding: '2px 8px', borderRadius: 99,
            }}>{preReg.length}</span>
            <span style={{ fontSize: 12, color: '#939598', marginLeft: 2 }}>pre-registered, not yet checked in</span>
          </div>
          {preReg.map(s => (
            <div key={s.id} style={{
              background: '#f8f7ff', border: '1.5px solid #ddd6fe', borderRadius: 4,
              padding: '8px 16px', marginBottom: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div className="sp-survivor-name">{s.firstName} {s.lastName}</div>
                  {s.qrCode && <div style={{ fontSize: 12, color: '#7c3aed', fontWeight: 600, marginTop: 1 }}>{s.qrCode}</div>}
                  {s.specialNeeds && (
                    <div style={{ fontSize: 12, color: '#b45309', marginTop: 3 }}>
                      ⚠ {s.specialNeeds.replace(/[\[\]"]/g, '')}
                    </div>
                  )}
                </div>
                <span style={{
                  background: '#ede9fe', color: '#7c3aed', fontSize: 12, fontWeight: 700,
                  padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap',
                }}>Pre-reg</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <div className="sp-drawer-overlay" onClick={() => setSelected(null)}>
          <div className="sp-drawer" onClick={e => e.stopPropagation()}>
            <div className="sp-drawer-handle" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div className="sp-drawer-title" style={{ marginBottom: 0 }}>{selected.firstName} {selected.lastName}</div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7B7D7F' }}>
                <IconX size={20} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16, fontSize: 14 }}>
              {[
                { label: 'DOB', value: selected.dateOfBirth || '—' },
                { label: 'Gender', value: selected.gender || '—' },
                { label: 'Phone', value: selected.phone || '—' },
                { label: 'Cot', value: selected.cotAssignment || '—' },
              ].map(({ label, value }) => (
                <div key={label} style={{ background: '#F8F8F8', borderRadius: 4, padding: '8px 8px' }}>
                  <div style={{ fontSize: 12, color: '#939598', fontWeight: 600, marginBottom: 2 }}>{label}</div>
                  <div style={{ fontWeight: 600, color: '#1B1C1C' }}>{value}</div>
                </div>
              ))}
            </div>

            {selected.emergencyContactName && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: '8px 16px', marginBottom: 16, fontSize: 14 }}>
                <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 2 }}>Emergency Contact</div>
                <div>{selected.emergencyContactName}</div>
                {selected.emergencyContactPhone && <div style={{ color: '#4B3FFF' }}>{selected.emergencyContactPhone}</div>}
              </div>
            )}

            {selected.specialNeeds && selected.specialNeeds !== '[]' && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, padding: '8px 16px', marginBottom: 16, fontSize: 14 }}>
                <div style={{ fontWeight: 700, color: '#991b1b', marginBottom: 2 }}>Special Needs</div>
                <div>{selected.specialNeeds.replace(/[\[\]"]/g, '')}</div>
              </div>
            )}

            {selected.notes && (
              <div style={{ background: '#F8F8F8', borderRadius: 4, padding: '8px 16px', marginBottom: 16, fontSize: 14 }}>
                <div style={{ fontWeight: 700, color: '#475569', marginBottom: 2 }}>Notes</div>
                <div>{selected.notes}</div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="sp-btn sp-btn-secondary sp-btn-sm" style={{ flex: 1 }} onClick={() => setSelected(null)}>Close</button>
              <button className="sp-btn sp-btn-danger sp-btn-sm" style={{ flex: 1 }} onClick={handleCheckout} disabled={checkingOut}>
                {checkingOut ? 'Checking out…' : 'Check Out'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
